// Fix: Populated file with type definitions
// NOTE: Client and Demo share the same portal access; Demo is legacy enum value.
export enum UserRole {
  SUPER_ADMIN = 'Super Admin',
  ADMIN = 'Admin',
  PRODUCER = 'Producer',
  PROJECT_MANAGER = 'Project Manager',
  COORDINATOR = 'Coordinator',
  SALES = 'Sales',  // Ограниченная роль - видит только свои проекты (где producer_id = userId)
  CLIENT = 'Client',
  DEMO = 'Demo',
}

export enum ProfileType {
  VFX_ARTIST = 'vfx_artist',
  PRODUCER = 'producer',
  SUPERVISOR = 'supervisor',
  COORDINATOR = 'coordinator',
  SALES = 'sales',
  FINDER = 'finder',
  CLIENT = 'client',
  ADMIN = 'admin',
}

/** Client portal roles (read-only, assigned projects only). */
export const isClientRole = (role: string | null | undefined): boolean =>
  role === UserRole.CLIENT || role === UserRole.DEMO;

export enum ViewMode {
  GRID = 'grid',
  LIST = 'list',
}

// Helper function to check if user can edit artists
export const canEditArtists = (role: string | null): boolean => {
  if (!role) return false;
  if (isClientRole(role)) return false;
  
  return ['Super Admin', 'Admin', 'Producer', 'Coordinator', 'Project Manager', 'Sales'].includes(role);
};

// Helper function to check if user can delete artists
export const canDeleteArtists = (role: string | null): boolean => {
  if (!role) return false;
  if (isClientRole(role) || role === 'Sales') return false;
  
  return ['Super Admin', 'Admin', 'Producer'].includes(role);
};

// Helper function to check if user is Sales
export const isSales = (role: string | null): boolean => {
  return role === 'Sales';
};

// Helper function to check if user has admin rights (Super Admin or Admin)
export const isAdmin = (role: string | null): boolean => {
  if (!role) return false;
  return ['Super Admin', 'Admin'].includes(role);
};

// Helper function to check if user is Demo (read-only)
export const isDemo = (role: string | null): boolean => isClientRole(role);

export const canViewArtistContacts = (role: string | null): boolean => {
  if (!role) return false;
  if (isClientRole(role)) return false;
  return true;
};

// Helper function to check if user is Super Admin
export const isSuperAdmin = (role: string | null): boolean => {
  return role === 'Super Admin';
};

// Helper function to check if user can edit finances
export const canEditFinances = (role: string | null): boolean => {
  // Only Super Admin can edit finances
  return role === 'Super Admin';
};

// Helper function to check if user can edit/delete a project
export const canModifyProject = (
  project: Project,
  currentUserId: string | undefined,
  currentRole: string | null
): boolean => {
  if (!currentUserId || !currentRole) return false;
  
  // Super Admin and Admin can edit/delete all projects (Admin may be scoped by preference)
  if (currentRole === 'Super Admin' || currentRole === 'Admin' || currentRole === 'Producer') {
    return true;
  }
  
  if (isClientRole(currentRole) && project.clientId === currentUserId) {
    return true;
  }
  
  // Project creator can edit/delete their own project
  if (project.createdBy === currentUserId) {
    return true;
  }
  
  return false;
};

export interface User {
  id: string;
  email: string;
  username?: string;
  profileId?: string | null;
  role: 'Super Admin' | 'Admin' | 'Producer' | 'Project Manager' | 'Coordinator' | 'Sales' | 'Client' | 'Demo';
  mustChangePassword?: boolean;
}

export interface UserData {
  id: string;
  username: string;
  email?: string;
  role: string;
  mustChangePassword?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  role: string;
  email?: string;
}

export enum SortOption {
  NAME_ASC = 'name-asc',
  NAME_DESC = 'name-desc',
  SPECIALIZATION_ASC = 'specialization-asc',
  SPECIALIZATION_DESC = 'specialization-desc',
}

export type ProjectStatus = 'Planning' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled' | 'MagicBox';
export type ProjectPriority = 'Urgent' | 'High' | 'Medium' | 'Low';
export type ProjectType = 'Feature Film' | 'TV Series' | 'Commercial' | 'Music Video' | 'Game Cinematic' | 'VR/AR' | 'Other';

export interface Project {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  status?: ProjectStatus;
  budget?: number; // May be hidden based on role
  currency?: string; // Currency for budget (USD, EUR, RUB, BYN)
  client?: string;
  clientId?: string;
  clientProfileId?: string;
  clientName?: string;
  color?: string;
  priority?: ProjectPriority;
  projectType?: ProjectType;
  tags?: string[];
  progress?: number;
  notes?: string;
  imageUrl?: string;
  producerId?: string;
  producerProfileId?: string;
  managerId?: string;
  managerProfileId?: string;
  coordinatorId?: string;
  coordinatorProfileId?: string;
  finderId?: string;
  finderProfileId?: string;
  producerName?: string; // Producer username (for display)
  managerName?: string; // Manager username (for display)
  coordinatorName?: string; // Coordinator username (for display)
  finderName?: string; // Finder username (for display)
  createdBy?: string; // User ID who created the project
  createdByUsername?: string; // Username of creator (for display)
  magicBoxId?: string | null; // MagicBox project ID if project was transferred to MagicBox
  magicboxProjectId?: string | null; // MagicBox project ID for linked projects
  magicboxProgress?: number | null; // Progress from MagicBox (0-100%)
  magicboxCompletedShots?: number | null; // Number of completed shots
  magicboxTotalShots?: number | null; // Total number of shots
  magicboxLastSync?: string | null; // Last sync timestamp
  taxRate?: number; // Tax Rate %
  prepaymentType?: 'PERCENT' | 'FIXED' | null; // Type of prepayment requirement
  prepaymentValue?: number | null; // Prepayment value (percentage 0-100 if PERCENT, or fixed amount if FIXED)
  approvedBiddingSheetName?: string; // Name of the approved bidding sheet
  hasApprovedExpenses?: boolean; // Whether project has any approved expense assignments (sent to finance)
  createdAt?: string;
  updatedAt?: string;
}

export interface BiddingSheet {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  currency?: string;
  videoStatus?: 'draft' | 'processing' | 'completed' | 'failed';
  status?: 'draft' | 'sent' | 'approved' | 'rejected'; // Approval status
  approvedBy?: string;
  approvedAt?: string;
  sourceSheetId?: string | null;
  revisionStatus?: 'pending' | 'rejected' | null;
  supersededBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  videoUrl?: string;
  shotCount?: number;
  totalHours?: number;
  totalCost?: number;
  valueColumnMode?: 'hours' | 'sum';
  producerPercentage?: number;
  producerFixedFee?: number;
  useProducerFixedFee?: boolean;
  coordinatorFee?: number;
  supervisorFee?: number;
  producerName?: string;
  coordinatorName?: string;
  supervisorName?: string;
  vatRate?: number;
  totalNet?: number;
  totalVat?: number;
  totalGross?: number;
  invoiceNumber?: string;
  contractNumber?: string;
  paymentDeadline?: string;
  paymentDate?: string;
  paymentStatus?: 'pending' | 'partial' | 'paid' | 'overdue';
  isRevenueSheet?: boolean; // TRUE for revenue (income) sheets, FALSE for expense sheets
  expenseSheetId?: string; // Reference to expense sheet (for revenue sheets)
  revenueSheetId?: string; // Reference to revenue sheet (for expense sheets)
  thumbnails?: string[];
  createdByName?: string;
  // Computed fields from project join
  projectPrepaymentType?: 'PERCENT' | 'FIXED';
  projectPrepaymentValue?: number;
  projectBudget?: number;
  projectTotalPaid?: number;
}

export interface ProjectArtist {
  id: string;
  projectId: string;
  artistId: string;
  role?: string;
  assignedAt: string;
  artistName?: string;
  specialization?: string;
  location?: string;
  avatarUrl?: string;
}

export interface Artist {
  id:string;
  name: string;
  specialization: string;
  software: string[];
  availability: string[]; // array of 'YYYY-MM-DD' strings
  portfolioUrl?: string;
  email: string;
  telegram?: string;
  avatarUrl?: string;
  description?: string;
  location?: string;
  hourlyRate?: number;
  profileType?: string;
  userId?: string | null;
  organization?: string | null;
  registrationStatus?: 'active' | 'pending' | 'rejected';
  assignedProjects: string[];
  // Deprecated: keeping for backward compatibility during migration
  contact?: string;
}

// Extended type for form with file upload
export interface ArtistFormData extends Omit<Artist, 'id'> {
  id?: string;
  avatarFile?: File;
}

// Drawing tool types for annotations (player uses first five; text/freehand for legacy editors)
export type DrawingTool = 'pencil' | 'eraser' | 'line' | 'square' | 'circle' | 'text' | 'freehand';

// Timecode interface (from MagicBox)
export interface Timecode {
  minutes: number;
  seconds: number;
  frames: number;
}

/** Lightweight user shape for @mentions in the review player */
export interface MentionUser {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string | null;
}

// Comment data interface (from MagicBox)
export interface CommentData {
  id: string;
  shotId: string;
  createdAt: string; // ISO string
  timecode: Timecode;
  timeInSeconds: number;
  text: string;
  authorId: string;
  authorName?: string; // User name for display
  authorAvatarUrl?: string | null;
  frameThumbnailUrl?: string; // Video frame at comment time
  drawingDataUrl?: string; // Drawing annotation overlay
  recordingUrl?: string; // For video review recordings
  duration?: number; // duration of the recording in seconds
  recordingDuration?: number; // alias used by backend
  parentCommentId?: string | null;
  rootCommentId?: string | null;
  replies?: CommentData[];
  attachmentUrl?: string;
  attachmentOriginalName?: string;
  lastActivityAt?: string;
}

// Shot annotation interface
export interface ShotAnnotation {
  id: string;
  type: DrawingTool;
  timestamp?: string; // Video timestamp (e.g., "00:01:23.45")
  frame?: number; // Frame number (optional)
  dataUrl: string; // Drawing as base64 PNG (data:image/png;base64,...)
  metadata?: {
    color?: string;
    strokeWidth?: number;
    tool?: string;
    comment?: string;
  };
  createdBy: string;
  createdAt: string;
}

// Shot interface
export interface Shot {
  id: string;
  biddingSheetId: string;
  shotNumber: number;
  thumbnailUrl?: string;
  videoUrl?: string; // Added for frontend use
  frameIn?: number;
  frameOut?: number;
  durationFrames?: number;
  timecodeInSeconds?: number;
  timecodeOutSeconds?: number;
  commentsCount?: number; // Number of comments/annotations on this shot
  shotName?: string; // User-defined shot name (e.g., SHOT_0010)
  description?: string | null;
  annotations?: ShotAnnotation[]; // Visual annotations
  annotationUpdatedAt?: string;
  annotationUpdatedBy?: string;
  rowVersion?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShotAssignment {
  id: string;
  shotId: string;
  artistId?: string | null;
  realArtistId?: string | null;
  specialization: string;
  estimatedHours?: number;
  hourlyRate?: number;
  realEstimatedHours?: number;
  realHourlyRate?: number;
  fixedAmount?: number | null;
  isVirtualArtist?: boolean;
  virtualArtistType?: string | null;
  artistLevel?: string; // e.g. "Junior", "Mid", "Lead"
  rowVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseAssignment {
  id: string;
  shotId: string;
  artistId: string | null; // Real artist ID
  specialization: string;
  estimatedHours: number;
  hourlyRate: number;
  fixedAmount: number | null;
  useFixedAmount: boolean; // Toggle between hours-based calc and fixed amount
  status?: 'Pending' | 'Approved' | 'Paid';
  isPaid?: boolean; // Whether this assignment has been paid (has OUT transaction)
  rowVersion: number;
  createdAt: string;
  updatedAt: string;
}